/**
 * A manual Support at Home plan, applied to the client's profile: the position once the plan is in
 * place. Moved out of StrategiesSection's memo, unchanged, so the Action Plan can fill the SA456 from
 * the same position for a plan saved earlier (owner, 3 Oct 2026).
 *
 * Funeral bonds, gifting, the home (downsize, renovate or upgrade), a reverse mortgage, the linked
 * aged care deposit and living expenses, then the annuity. HEAS deliberately changes nothing here.
 */
import { applyRadFundingToFactFind } from '../../strategy/StrategyIntegrationUtils.ts';
import { applyAnnuityToFactFind } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { recordDatedGift } from '../../../../shared/src/calculation/giftingDeprivation.ts';
import { applyDownsizeToFactFind, applyRenovateToFactFind, applyUpgradeToFactFind } from './SupportAtHomeHelpers.tsx';
import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import type { DownsizeDetails as StrategyDownsizeDetails } from '../../strategy/supportAtHomeStrategyFunding.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';
import { initializeAbpProjectionNomineesOnFactFind, markAbpFundedAcquisitionAfterLiquidation } from '../../../../shared/src/calculation/abpProjectionUtils.ts';

/** What a manual Support at Home plan is made of, as its save records it (loosely typed, as the page's state is). */
export interface ManualSahPlanInputs {
    manualStrategies: { gifting: number | string; funeralBonds: number | string; giftingSource?: unknown; funeralSource?: unknown };
    propertyStrategy: unknown;
    downsizeDetails: any;
    renovateDetails: any;
    upgradeDetails: any;
    overallStrategy: any;
    reverseMortgageDetails: any;
    selectedAgedCareIntegration: any;
    selectedFundingStrategy: unknown;
    annuityRecordForCalculation: any;
    /** Today's financials, which the renovate and upgrade appliers read. */
    beforeFinancials: any;
}

export function simulateManualSahPlan(factFind: DraftFactFind, inputs: ManualSahPlanInputs): { optimizedFactFind: DraftFactFind; agedCareAnnualCost: number } {
    const { manualStrategies, propertyStrategy, downsizeDetails, renovateDetails, upgradeDetails, overallStrategy, reverseMortgageDetails, selectedAgedCareIntegration, selectedFundingStrategy, annuityRecordForCalculation, beforeFinancials } = inputs;
    let optimizedFactFind: DraftFactFind = structuredClone(factFind);

    // Helper to reduce asset value based on source
    const reduceAssetValue = (assetList: { value?: number | string }[], amountToReduce: number | string) => {
        let remaining = Number(amountToReduce) || 0;
        for (const asset of assetList) {
            if (remaining <= 0) break;
            const currentValue = parseFloat(String(asset.value)) || 0;
            const reduction = Math.min(currentValue, remaining);
            asset.value = currentValue - reduction;
            remaining -= reduction;
        }
    };

    // Apply funeral bonds (add to exempt assets AND reduce source asset)
    if (Number(manualStrategies.funeralBonds) > 0) {
        if (!optimizedFactFind.assets) optimizedFactFind.assets = {};
        if (!optimizedFactFind.assets.funeralBonds) {
            optimizedFactFind.assets.funeralBonds = [];
        }
        optimizedFactFind.assets.funeralBonds.push({
            id: 'opt-funeral-bond',
            type: 'funeralBonds',
            description: 'Optimised Funeral Bond',
            value: Number(manualStrategies.funeralBonds) || 0,
            ownership: 'client'
        });

        if (manualStrategies.funeralSource === 'bank' && optimizedFactFind.assets.bankAccounts) {
            reduceAssetValue(optimizedFactFind.assets.bankAccounts, manualStrategies.funeralBonds);
        } else if (manualStrategies.funeralSource === 'abp' && optimizedFactFind.assets.accountBasedPensions) {
            reduceAssetValue(optimizedFactFind.assets.accountBasedPensions, manualStrategies.funeralBonds);
        } else if (manualStrategies.funeralSource === 'shares' && optimizedFactFind.assets.shares) {
            reduceAssetValue(optimizedFactFind.assets.shares, manualStrategies.funeralBonds);
        }
    }

    // Apply gifting: add to giftsHistory AND reduce source asset
    if (Number(manualStrategies.gifting) > 0) {
        // Dated to today (24 Oct 2026). It was keyed by the CALENDAR year, which put an October gift in
        // the previous financial year; the five years now run from the day.
        recordDatedGift(optimizedFactFind as never, Number(manualStrategies.gifting), new Date());

        if (manualStrategies.giftingSource === 'bank' && optimizedFactFind.assets?.bankAccounts) {
            reduceAssetValue(optimizedFactFind.assets.bankAccounts, manualStrategies.gifting);
        } else if (manualStrategies.giftingSource === 'abp' && optimizedFactFind.assets?.accountBasedPensions) {
            reduceAssetValue(optimizedFactFind.assets.accountBasedPensions, manualStrategies.gifting);
        } else if (manualStrategies.giftingSource === 'shares' && optimizedFactFind.assets?.shares) {
            reduceAssetValue(optimizedFactFind.assets.shares, manualStrategies.gifting);
        }
    }

    // Apply downsizing changes if selected. applyDownsizeToFactFind wants the strict (number-only)
    // supportAtHomeStrategyFunding.ts DownsizeDetails; downsizeDetails here is the wider Draft shape
    // edited via CurrencyInput (number | string mid-edit), so the cast mirrors the Draft->strict
    // pattern used throughout this migration (see factFindTypes.ts header comment).
    if (propertyStrategy === 'downsize') {
        optimizedFactFind = applyDownsizeToFactFind(optimizedFactFind, downsizeDetails as unknown as Partial<StrategyDownsizeDetails>);
    }

    // Apply renovate strategy if selected
    if (propertyStrategy === 'renovate' && Number(renovateDetails.renovationAmount) > 0) {
        optimizedFactFind = applyRenovateToFactFind(optimizedFactFind, renovateDetails, beforeFinancials ?? undefined);
    }

    // Apply upgrade strategy if selected
    if (propertyStrategy === 'upgrade' && Number(upgradeDetails.newHomePurchasePrice) > 0) {
        optimizedFactFind = applyUpgradeToFactFind(optimizedFactFind, upgradeDetails, beforeFinancials ?? undefined);
    }

    // Apply reverse mortgage strategy
    if ((overallStrategy.reverseMortgage || propertyStrategy === 'reverseMortgage') && reverseMortgageDetails) {
        const rmDetails = reverseMortgageDetails;
        const loanAmount = parseFloat(String(rmDetails.loanAmount)) || 0;
        const establishmentFees = parseFloat(String(rmDetails.establishmentFees)) || 0;
        const netProceeds = Math.max(0, loanAmount - establishmentFees);


        if (rmDetails.loanStructure === 'lump_sum' && netProceeds > 0) {
            if (!optimizedFactFind.assets) optimizedFactFind.assets = {};
            if (!optimizedFactFind.assets.bankAccounts) optimizedFactFind.assets.bankAccounts = [];
            if (optimizedFactFind.assets.bankAccounts.length > 0) {
                const firstBankAccount = optimizedFactFind.assets.bankAccounts[0];
                const currentBalance = parseFloat(String(firstBankAccount.value)) || 0;
                firstBankAccount.value = currentBalance + netProceeds;
            } else {
                optimizedFactFind.assets.bankAccounts.push({
                    id: 'reverse-mortgage-proceeds',
                    type: 'bankAccounts',
                    description: 'Reverse Mortgage Proceeds',
                    value: netProceeds,
                    ownership: 'client',
                    // Interest, not capital growth — see bankPlugin.ts's DEFAULT_BANK_GROWTH_RATE_PERCENT.
                    incomeRate: 2,
                    growthRate: 0
                });
            }
        }

        // Add reverse mortgage as a liability (reduces net worth, but NO repayments - interest compounds)
        // Reverse mortgage doesn't require repayments - the loan compounds against home equity
        if (loanAmount > 0) {
            if (!optimizedFactFind.liabilities) optimizedFactFind.liabilities = {};
            if (!optimizedFactFind.liabilities.loans) optimizedFactFind.liabilities.loans = [];

            // Check if reverse mortgage liability already exists
            const rmLiability = optimizedFactFind.liabilities.loans.find(l => l.id === 'reverse-mortgage');
            if (rmLiability) {
                rmLiability.balance = loanAmount;
                rmLiability.interestRate = Number(rmDetails.interestRate) || REVERSE_MORTGAGE_DEFAULT_RATE;
                // NO repayments field - reverse mortgage doesn't require repayments
                rmLiability.repayments = 0;
                rmLiability.excludeFromCashflow = true; // Flag to exclude from cashflow calculations
            } else {
                optimizedFactFind.liabilities.loans.push({
                    id: 'reverse-mortgage',
                    type: 'reverse_mortgage',
                    description: 'Reverse Mortgage',
                    balance: loanAmount,
                    interestRate: Number(rmDetails.interestRate) || REVERSE_MORTGAGE_DEFAULT_RATE,
                    repayments: 0, // No repayments - interest compounds against home equity
                    excludeFromCashflow: true // Flag to exclude from cashflow calculations
                });
            }
        }
    }
    
    // HEAS deliberately makes NO balance-sheet change here. Starting it moves no asset: payments
    // arrive fortnightly over time, the loan starts at zero, and the payments are not
    // means-tested income. Both `afterFinancials` (calculateFinancials) and the projections
    // receive `heas`/`heasDetails` in their strategy options, which is where it is modelled.
    // (An earlier version deducted a year of payments from the bank account on day one — a
    // phantom withdrawal that inflated the pension and understated the estate.)

    if (selectedAgedCareIntegration?.radFunding) {
        optimizedFactFind = applyRadFundingToFactFind(optimizedFactFind, selectedAgedCareIntegration.radFunding);
    }

    if (selectedAgedCareIntegration?.livingExpenses !== undefined && selectedAgedCareIntegration?.livingExpenses !== null) {
        optimizedFactFind.expenses = {
            ...optimizedFactFind.expenses,
            livingExpenses: selectedAgedCareIntegration.livingExpenses
        };
    }

    if (selectedFundingStrategy === 'annuity' && annuityRecordForCalculation) {
        optimizedFactFind = applyAnnuityToFactFind(optimizedFactFind as unknown as FactFind, annuityRecordForCalculation) as unknown as DraftFactFind;
    }

    alignAbpDrawsWithEngine(factFind, optimizedFactFind);

    return {
        optimizedFactFind,
        agedCareAnnualCost: selectedAgedCareIntegration?.annualCost || 0
    };
}

/**
 * A PENSION ACCOUNT THE PLAN DRAWS ON IS PAID AT THE MINIMUM AFTERWARDS — the engine's rule
 * (liquidateFundingSources → markAbpFundedAcquisitionAfterLiquidation), applied here so a plan built by
 * hand and the same plan from the engine give the same figures (19 Oct 2026, caught live: "Spend
 * $250,000 on the home" left $16,558 a year by hand and $9,604 from the engine, because the hand path
 * kept Colin's nominated $X a year on a balance $100,000 smaller). Untouched accounts keep the draw on
 * the file, as the engine keeps them.
 */
function alignAbpDrawsWithEngine(original: DraftFactFind, planned: DraftFactFind): void {
    const before = new Map((original?.assets?.accountBasedPensions || []).map((a) => [String(a.id), Number(a.value ?? a.balance ?? 0)]));
    const after = (planned?.assets?.accountBasedPensions || []) as unknown as FactFindAsset[];
    if (!after.length) return;
    initializeAbpProjectionNomineesOnFactFind(planned as unknown as FactFind);
    after.forEach((asset) => {
        if (asset.__abpFundedAcquisition === true) return;
        const was = before.get(String(asset.id));
        const now = Number(asset.value ?? 0);
        if (was != null && now > 0 && now < was - 0.5) {
            markAbpFundedAcquisitionAfterLiquidation(asset, planned as unknown as FactFind, 1);
        }
    });
}
